import { useEffect, useMemo, useState } from 'react';
import Popup from '../../other/Popup';
import { postAPI } from '../../../utils/utils';
import { can } from '../../../utils/permissions';
import {
    DEFAULT_REFERENCE_LABEL,
    MAX_REFERENCE_LABEL_LENGTH,
    REFERENCE_EMOJI_OPTIONS,
    getEffectiveReferenceLabel,
    getEffectiveReferenceEmoji,
    sanitizeReferenceLabel,
} from '../../../utils/referenceLabel';
import './AjuntamentModal.css';

const DIRECTIONS = [
    { value: 0, position: 'top', name: 'a dalt' },
    { value: 1, position: 'right', name: 'a la dreta' },
    { value: 2, position: 'bottom', name: 'a baix' },
    { value: 3, position: 'left', name: "a l'esquerra" },
];

// Dues pantalles: la principal per l'ús diari (posició + triar opció guardada)
// i la de gestió (crear, esborrar, per defecte de colla), només per a caps.
function AjuntamentModal(props) {
    const {
        closed, setClosed,
        socket, room,
        ajuntament, setAjuntament,
        ajuntamentLabel, setAjuntamentLabel,
        ajuntamentEmoji, setAjuntamentEmoji,
        pissarraConfig,
        refreshPissarraConfig,
        userInfo,
    } = props;

    const [view, setView] = useState('main');
    const [formOpen, setFormOpen] = useState(false);
    const [draftLabel, setDraftLabel] = useState('');
    const [draftEmoji, setDraftEmoji] = useState('');
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

    const canManage = can(userInfo, 'MANAGE_REFERENCE_OPTIONS');

    const configLabel = pissarraConfig?.ajuntamentLabel || '';
    const configEmoji = pissarraConfig?.ajuntamentEmoji || '';
    const savedOptions = pissarraConfig?.savedOptions || [];

    // Referència ACTIVA de la prova, resolta amb el valor per defecte de la colla
    const effectiveLabel = getEffectiveReferenceLabel(ajuntamentLabel, configLabel);
    const effectiveEmoji = getEffectiveReferenceEmoji(ajuntamentLabel, ajuntamentEmoji, configEmoji);

    const clearDraft = () => {
        setDraftLabel('');
        setDraftEmoji('');
        setEmojiPickerOpen(false);
    };

    useEffect(() => {
        if (!closed) {
            setView('main');
            setFormOpen(false);
            clearDraft();
            if (refreshPissarraConfig) refreshPissarraConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closed]);

    // Única via per canviar la referència de la prova: explícita i instantània
    const applyReference = (rawLabel, rawEmoji) => {
        const label = sanitizeReferenceLabel(rawLabel);
        const emoji = (rawEmoji || '').trim();

        setAjuntamentLabel(label);
        setAjuntamentEmoji(emoji);

        socket.emit('.set_ajuntament_label_to_room', room, label, emoji);
    };

    const setDirection = (value) => {
        if (value === ajuntament) return;

        setAjuntament(value);
        socket.emit('.rotate_ajuntament_to_room', room, value);
    };

    const chipOptions = useMemo(() => {
        const options = savedOptions
            .filter(option => option && typeof option.label === 'string' && option.label.trim() !== '')
            .map(option => ({ label: option.label, emoji: option.emoji || '', builtin: false }));

        const hasAjuntament = options
            .some(option => option.label.trim().toLowerCase() === DEFAULT_REFERENCE_LABEL.toLowerCase());

        return hasAjuntament
            ? options
            : [{ label: DEFAULT_REFERENCE_LABEL, emoji: '', builtin: true }, ...options];
    }, [savedOptions]);

    const isChipActive = (chip) =>
        effectiveLabel.toLowerCase() === chip.label.trim().toLowerCase();

    const isChipDefault = (chip) =>
        configLabel === ''
            ? chip.label.trim().toLowerCase() === DEFAULT_REFERENCE_LABEL.toLowerCase()
            : chip.label.trim().toLowerCase() === configLabel.trim().toLowerCase();

    const applyChip = (chip) => {
        // El xip clàssic "Ajuntament" només treu l'override si la colla no té
        // valor per defecte propi; si en té, s'aplica "Ajuntament" explícitament
        if (chip.builtin && configLabel === '') {
            applyReference('', '');
        } else if (chip.builtin) {
            applyReference(DEFAULT_REFERENCE_LABEL, '');
        } else {
            applyReference(chip.label, chip.emoji);
        }
    };

    // Estrella de la pantalla de gestió: fa aquesta opció el per defecte de la
    // colla. La builtin restaura el clàssic (label buit = esborrar el default).
    // No toca l'override de la prova actual.
    const setChipAsDefault = (chip) => {
        if (isChipDefault(chip)) return;

        const payload = chip.builtin
            ? { label: '', emoji: '' }
            : { label: chip.label, emoji: chip.emoji || '' };

        postAPI('/ajuntament_default_label', payload, () => {
            if (refreshPissarraConfig) refreshPissarraConfig();
        });
    };

    const deleteOption = (chip) => {
        const confirmed = window.confirm(`Segur que vols esborrar "${chip.label}" de les opcions guardades de la colla?`);
        if (!confirmed) return;

        postAPI('/delete_ajuntament_option', { label: chip.label }, () => {
            if (refreshPissarraConfig) refreshPissarraConfig();
        });
    };

    const trimmedDraft = sanitizeReferenceLabel(draftLabel);
    const normalizedDraftEmoji = (draftEmoji || '').trim();
    const canSaveDraft = trimmedDraft !== '' && !savedOptions.some(option =>
        option.label.trim().toLowerCase() === trimmedDraft.toLowerCase() &&
        (option.emoji || '') === normalizedDraftEmoji
    );

    // Desa a la biblioteca de la colla; NO toca la prova (per aplicar-la, torna
    // a la pantalla principal i toca el chip)
    const saveDraft = () => {
        if (!canSaveDraft) return;

        postAPI('/save_ajuntament_option', { label: trimmedDraft, emoji: normalizedDraftEmoji }, () => {
            if (refreshPissarraConfig) refreshPissarraConfig();
        });
        clearDraft();
        setFormOpen(false);
    };

    return (
        <Popup
            closed={closed}
            setClosed={setClosed}
            className="ajuntament-modal"
            width="min(92vw, 440px)"
            maxWidth="440px"
            maxHeight="90vh"
        >
            {view === 'main' ? (
                <>
                    <h2 className="ajuntament-modal-title">Punt de referència</h2>
                    <p className="ajuntament-modal-subtitle">
                        Tria a quina vora de la pissarra queda i com voleu que es mostri.
                    </p>

                    <div className="ajuntament-modal-board" role="radiogroup" aria-label="Posició del punt de referència">
                        {DIRECTIONS.map(({ value, position, name }) => (
                            <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={ajuntament === value}
                                aria-label={`Punt de referència ${name}`}
                                className={`ajuntament-modal-edge ajuntament-modal-edge-${position} ${ajuntament === value ? 'ajuntament-modal-edge-active' : ''}`}
                                onClick={() => setDirection(value)}
                            >
                                {ajuntament === value ? [effectiveEmoji, effectiveLabel].filter(Boolean).join(' ') : '+'}
                            </button>
                        ))}
                        <div className="ajuntament-modal-pinya">Pinya</div>
                    </div>

                    <div className="ajuntament-modal-saved">
                        <span className="ajuntament-modal-section-title">Opcions guardades</span>

                        <div className="ajuntament-modal-chips">
                            {chipOptions.map(chip => (
                                <div
                                    key={chip.label}
                                    role="button"
                                    tabIndex={0}
                                    className={`ajuntament-modal-chip ${isChipActive(chip) ? 'ajuntament-modal-chip-active' : ''}`}
                                    onClick={() => applyChip(chip)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            applyChip(chip);
                                        }
                                    }}
                                >
                                    {isChipDefault(chip) ? (
                                        <span className="ajuntament-modal-chip-star" title="Opció per defecte de la colla">★</span>
                                    ) : null}
                                    {chip.emoji ? (
                                        <span className="ajuntament-modal-chip-emoji">{chip.emoji}</span>
                                    ) : null}
                                    <span className="ajuntament-modal-chip-label">{chip.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {canManage ? (
                        <div className="ajuntament-modal-footer">
                            <button
                                type="button"
                                className="ajuntament-modal-manage-link"
                                onClick={() => setView('manage')}
                            >
                                <span>⚙ Gestiona les opcions…</span>
                                <span className="ajuntament-modal-manage-chevron">›</span>
                            </button>
                        </div>
                    ) : null}
                </>
            ) : (
                <>
                    <div className="ajuntament-modal-manage-header">
                        <button
                            type="button"
                            className="ajuntament-modal-back-btn"
                            onClick={() => setView('main')}
                        >
                            ← Torna
                        </button>
                        <h2 className="ajuntament-modal-title ajuntament-modal-manage-title">Gestiona les opcions</h2>
                    </div>
                    <p className="ajuntament-modal-subtitle">
                        L'estrella marca l'opció per defecte de la colla. Esborrar una opció no canvia cap pinya.
                    </p>

                    <div className="ajuntament-modal-option-rows">
                        {chipOptions.map(chip => (
                            <div key={chip.label} className="ajuntament-modal-option-row">
                                <span className="ajuntament-modal-option-emoji">{chip.emoji || '—'}</span>
                                <span className="ajuntament-modal-option-label">{chip.label}</span>

                                <button
                                    type="button"
                                    className={`ajuntament-modal-star-btn ${isChipDefault(chip) ? 'ajuntament-modal-star-btn-default' : ''}`}
                                    aria-label={isChipDefault(chip) ? 'Opció per defecte de la colla' : `Fes "${chip.label}" l'opció per defecte de la colla`}
                                    title={isChipDefault(chip) ? 'Opció per defecte de la colla' : 'Fes-la per defecte de la colla'}
                                    disabled={isChipDefault(chip)}
                                    onClick={() => setChipAsDefault(chip)}
                                >
                                    {isChipDefault(chip) ? '★' : '☆'}
                                </button>

                                {!chip.builtin ? (
                                    <button
                                        type="button"
                                        className="ajuntament-modal-row-delete"
                                        aria-label={`Esborra "${chip.label}" de les opcions guardades`}
                                        title="Esborra de les opcions guardades"
                                        onClick={() => deleteOption(chip)}
                                    >
                                        🗑
                                    </button>
                                ) : (
                                    <span className="ajuntament-modal-row-delete-placeholder" />
                                )}
                            </div>
                        ))}
                    </div>

                    {!formOpen ? (
                        <button
                            type="button"
                            className="ajuntament-modal-new-option-btn"
                            onClick={() => setFormOpen(true)}
                        >
                            + Nova opció
                        </button>
                    ) : (
                        <>
                            <label className="ajuntament-modal-section-title" htmlFor="ajuntament-modal-input">
                                Nova opció
                            </label>
                            <div className="ajuntament-modal-input-wrapper">
                                <div className="ajuntament-modal-input-row">
                                    <button
                                        type="button"
                                        className={`ajuntament-modal-emoji-btn ${normalizedDraftEmoji === '' ? 'ajuntament-modal-emoji-btn-empty' : ''}`}
                                        aria-label="Tria la icona"
                                        title="Tria la icona"
                                        onClick={() => setEmojiPickerOpen(open => !open)}
                                    >
                                        {normalizedDraftEmoji === '' ? (
                                            <svg
                                                className="ajuntament-modal-icon-placeholder"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <circle cx="12" cy="12" r="9" />
                                                <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
                                                <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
                                                <path d="M8.5 14c1 1.25 2.17 1.88 3.5 1.88S14.5 15.25 15.5 14" />
                                            </svg>
                                        ) : normalizedDraftEmoji}
                                    </button>
                                    <input
                                        id="ajuntament-modal-input"
                                        className="ajuntament-modal-input"
                                        type="text"
                                        maxLength={MAX_REFERENCE_LABEL_LENGTH}
                                        value={draftLabel}
                                        placeholder="p. ex. Font"
                                        autoFocus
                                        onChange={e => setDraftLabel(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                saveDraft();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="ajuntament-modal-clear"
                                        aria-label="Tanca el formulari"
                                        title="Tanca el formulari"
                                        onClick={() => {
                                            clearDraft();
                                            setFormOpen(false);
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>

                                {emojiPickerOpen ? (
                                    <>
                                        <div className="ajuntament-modal-emoji-backdrop" onClick={() => setEmojiPickerOpen(false)} />
                                        <div className="ajuntament-modal-emoji-popover" role="listbox" aria-label="Icona del punt de referència">
                                            <button
                                                type="button"
                                                className={`ajuntament-modal-emoji-option ajuntament-modal-emoji-none ${normalizedDraftEmoji === '' ? 'ajuntament-modal-emoji-selected' : ''}`}
                                                onClick={() => {
                                                    setDraftEmoji('');
                                                    setEmojiPickerOpen(false);
                                                }}
                                            >
                                                Cap
                                            </button>
                                            {REFERENCE_EMOJI_OPTIONS.map(emoji => (
                                                <button
                                                    key={emoji}
                                                    type="button"
                                                    className={`ajuntament-modal-emoji-option ${normalizedDraftEmoji === emoji ? 'ajuntament-modal-emoji-selected' : ''}`}
                                                    onClick={() => {
                                                        setDraftEmoji(emoji);
                                                        setEmojiPickerOpen(false);
                                                    }}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : null}

                                <div className="ajuntament-modal-draft-actions">
                                    <button
                                        type="button"
                                        className="ajuntament-modal-primary-btn"
                                        disabled={!canSaveDraft}
                                        onClick={saveDraft}
                                    >
                                        Desa
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </Popup>
    );
}

export default AjuntamentModal;
